import { useEffect, useRef, useState } from "react";
import { BUILD_INFO } from "../buildInfo";

type VersionPayload = { version: string; commit: string; buildTime: string };

const fetchVersion = async (): Promise<VersionPayload | null> => {
  try {
    const res = await fetch("/version.json", {
      cache: "no-store",
      headers: { pragma: "no-cache", "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    return (await res.json()) as VersionPayload;
  } catch {
    return null;
  }
};

export function useBuildUpdateCheck(intervalMs = 60000) {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [serverInfo, setServerInfo] = useState<VersionPayload | null>(null);
  const timerRef = useRef<number | null>(null);

  const isNewer = (remote: VersionPayload) => {
    const localT = Date.parse(BUILD_INFO.buildTime);
    const remoteT = Date.parse(remote.buildTime);
    if (Number.isFinite(localT) && Number.isFinite(remoteT))
      return remoteT > localT;
    return remote.commit && remote.commit !== BUILD_INFO.commit;
  };

  useEffect(() => {
    const tick = async () => {
      const v = await fetchVersion();
      if (v) {
        setServerInfo(v);
        if (isNewer(v)) setHasUpdate(true);
      }
    };

    tick();

    // store id
    timerRef.current = window.setInterval(
      tick,
      intervalMs
    ) as unknown as number;

    // ALWAYS return void
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null; // optional: reset
      }
    };
  }, [intervalMs]);

  return {
    hasUpdate,
    serverInfo,
    localInfo: BUILD_INFO,
    clear: () => setHasUpdate(false),
  };
}
